import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useConversations from "admin/hooks/useConversations";
import type Conversation from "admin/types/interfaces/conversation";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, MessageSquare, Plus } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { nextgenConversationPath, nextgenProjectChatsPath } from "~/pages/manifold-nextgen/paths";
import { Agent } from "~/services/agents";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { DeleteConversationDialog } from "./DeleteConversationDialog";
import { ShareConversationDialog } from "./ShareConversationDialog";
import { useProjectContext } from "./useProjectContext";
import { useSessionActions } from "./useSessionActions";

const PAGE_SIZE = 20;

interface SessionListRowProps {
  projectId: string;
  session: Conversation;
  agent: Agent | undefined;
  isRenaming: boolean;
  onStartRename: () => void;
  onCommitRename: (next: string) => void;
  onEndRename: () => void;
  onShare: () => void;
  onDelete: () => void;
}

const SessionListRow = ({
  projectId,
  session,
  agent,
  isRenaming,
  onStartRename,
  onCommitRename,
  onEndRename,
  onShare,
  onDelete,
}: SessionListRowProps) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(session.displayName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming) {
      setDraft(session.displayName);
      // A plain rAF loses this race to Radix's own focus-return-to-trigger
      // behavior when the dropdown menu closes. setTimeout (a macrotask)
      // reliably runs after that, so our focus call wins instead.
      const id = window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [isRenaming, session.displayName]);

  const commit = () => onCommitRename(draft);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndRename();
    }
  };

  return (
    <TableRow
      className="group cursor-pointer"
      onClick={() => {
        if (!isRenaming) {
          navigate(nextgenConversationPath(projectId, session.id));
        }
      }}
    >
      <TableCell>
        <div className="relative min-h-5 min-w-0">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              onClick={(e) => e.stopPropagation()}
              aria-label="Rename session"
              className="absolute inset-0 rounded border border-border bg-background px-1 text-sm font-medium text-foreground outline-none focus:border-primary"
            />
          ) : (
            <span className="block truncate text-sm font-medium">{session.displayName}</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          {agent ? (
            <AgentAvatar agent={agent} size={20} />
          ) : (
            <div
              className="flex size-5 shrink-0 items-center justify-center text-muted-foreground"
              aria-hidden="true"
            >
              <MessageSquare className="size-3.5" />
            </div>
          )}
          <span className="truncate text-[13px] text-muted-foreground">
            {agent?.display_name ?? "—"}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <span className="truncate text-[13px] text-muted-foreground">
          {formatDistanceToNowStrict(new Date(session.updatedAt), { addSuffix: true })}
        </span>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex min-h-6 items-center justify-end">
          {!isRenaming && (
            <ConversationActionsMenu
              onShare={onShare}
              onRename={onStartRename}
              onDelete={onDelete}
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export const ProjectSessionsList = () => {
  const { projectId, attachedAgents } = useProjectContext();
  const [page, setPage] = useState(1);
  const [agentFilter, setAgentFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const agentIdFilter = agentFilter === "all" ? undefined : agentFilter;

  const {
    data: sessions,
    total,
    isLoading,
    mutate: search,
  } = useConversations({
    projectId,
    agentId: agentIdFilter,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    sortKey: "updated_at",
    sortOrder,
    nextgenOnly: true,
    isAdmin: false,
  });

  const refetch = () => search({ resource_id: projectId, agent_id: agentIdFilter });

  const {
    renamingId,
    startRename,
    commitRename,
    endRename,
    actionTarget,
    setActionTarget,
    confirmDelete,
  } = useSessionActions(projectId, { onMutated: refetch });

  const agentsById = new Map((attachedAgents ?? []).map((a) => [a.id, a]));
  const pageCount = total ? Math.ceil(total / PAGE_SIZE) : 1;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader
          title="All Sessions"
          context={total !== undefined ? `${total} session${total === 1 ? "" : "s"}` : undefined}
          action={
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary/80"
            >
              <Link to={nextgenProjectChatsPath(projectId)}>
                <Plus className="size-4" />
                New Session
              </Link>
            </Button>
          }
        />

        <div className="flex justify-start">
          <Select
            value={agentFilter}
            onValueChange={(value) => {
              setAgentFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger aria-label="Filter by agent" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agents</SelectItem>
              {(attachedAgents ?? []).map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-3" aria-busy="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="size-5 shrink-0 rounded-full" />
                <Skeleton className="h-4 w-48" />
              </div>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <p className="px-4 py-3 text-sm italic text-muted-foreground">No sessions yet.</p>
        ) : (
          <>
            <div className="rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session</TableHead>
                    <TableHead className="w-56">Agent</TableHead>
                    <TableHead className="w-40 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
                          setPage(1);
                        }}
                        className="inline-flex items-center gap-1 cursor-pointer transition-colors hover:text-foreground"
                      >
                        Updated
                        {sortOrder === "asc" ? (
                          <ArrowUp className="size-3.5" aria-hidden="true" />
                        ) : (
                          <ArrowDown className="size-3.5" aria-hidden="true" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-10">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <SessionListRow
                      key={session.id}
                      projectId={projectId}
                      session={session}
                      agent={session.agentId ? agentsById.get(session.agentId) : undefined}
                      isRenaming={renamingId === session.id}
                      onStartRename={() => startRename(session.id)}
                      onCommitRename={(next) =>
                        commitRename({ id: session.id, display_name: session.displayName }, next)
                      }
                      onEndRename={endRename}
                      onShare={() =>
                        setActionTarget({
                          session: { id: session.id, display_name: session.displayName },
                          action: "share",
                        })
                      }
                      onDelete={() =>
                        setActionTarget({
                          session: { id: session.id, display_name: session.displayName },
                          action: "delete",
                        })
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {pageCount > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {pageCount}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Previous page"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Next page"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === pageCount}
                  >
                    <ChevronRight />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ShareConversationDialog
        open={actionTarget?.action === "share"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationId={actionTarget?.session.id ?? ""}
      />

      <DeleteConversationDialog
        open={actionTarget?.action === "delete"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationTitle={actionTarget?.session.display_name ?? ""}
        onConfirm={confirmDelete}
      />
    </div>
  );
};
